/**
 * UserMenu.jsx
 * FILE VERSION: USERMENU-v1.7.41.197
 *
 * Real user dropdown for the top-right SD/Shane trigger.
 *
 * #1805 (2026-08-24, Stinger direct, approved via live mockup iteration
 * earlier tonight) -- real rebuild:
 *   1. Ornate gold "WELL REPD" wordmark image removed entirely -- same
 *      real reasoning as removing the "Shane" nameplate on #1796: idle
 *      visual weight, not functional content.
 *   2. Flat navy/gold treatment via THEME_2026 tokens (the same real,
 *      locked design-system palette used across tonight's #1797/#1798/
 *      #1802 work) instead of a white dropdown -- was the one remaining
 *      white surface in an otherwise navy/gold header.
 *   3. My Dashboard row flatter -- kept the star accent, dropped the
 *      heavy tinted block background for a plain trailing dot instead,
 *      matching the Viewing As / Switch Persona rows below it.
 *   4. Viewing As uses simple dot indicators throughout (hollow for
 *      inactive, filled gold for active) rather than only marking the
 *      active row.
 *   5. Settings/Sign Out use real inline Tabler-style outline SVGs
 *      (stroke-based, 24×24, rounded caps) instead of emoji.
 *   6. Version/health footer is a visually distinct, darker strip
 *      (THEME_2026.primaryBg, darker than the menu's own cardSidebar
 *      background) so it reads as a separate zone, not another item.
 *
 * [Goose 2026-08-24] REAL ADDITION, supersedes #1800 as a standalone
 * element -- HeaderPersonaChip (previously its own separate dropdown in
 * App.js) is removed; its real partner-switch list is folded in here as
 * its own "Switch Persona" section. Team-member switching ("Viewing
 * As", super-admin only) stays a separate section below it -- two
 * different real concepts (which partner you're sending as, vs. whose
 * dashboard you're previewing), now living in one dropdown instead of
 * two.
 *
 * [Hollywood, real fix] #1822, Stinger direct, resurfaced after a real
 * screenshot showed every single active partner (20+) rendering flat
 * in this small dropdown -- "too many in this small area i only want
 * selected partners... requested that was selectable on their partner
 * profile." Switch Persona now defaults to a curated quickPersonaList
 * (partners.quick_picker=true, set per-partner from PartnersManager.jsx
 * or right here via the new star toggle in Manage mode) plus whichever
 * partner is currently active, so you never lose sight of where you
 * are. Falls back to the full list automatically until at least one
 * partner has actually been curated, so this can't ship as an empty,
 * unusable switcher on day one.
 */

import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import SystemHealthDot from './SystemHealthDot';
import { THEME_2026 } from '../lib/theme2026';
import { downloadScout, useScoutVersion } from '../lib/scoutDownload';

// Real ordinal date ("Saturday, August 9th") -- toLocaleDateString has no
// ordinal option, so the suffix is built explicitly.
function ordinal(n) {
  if (n > 3 && n < 21) return 'th';
  switch (n % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function longDate(d = new Date()) {
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const day = d.getDate();
  return `${weekday}, ${month} ${day}${ordinal(day)}`;
}

// #1805 -- real, hand-built inline SVGs in the Tabler stroke-outline
// convention (24×24, stroke='currentColor', strokeWidth 1.75, rounded
// caps/joins) -- lucide-react is deliberately not a dependency right
// now (removed for the sidebar's custom-art system, confirmed while
// working #1804 tonight), so these are plain inline paths rather than
// an icon-library import, kept intentionally simple/geometric to be
// reliably correct rather than risk a malformed complex path.
function IconSettings({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="7.5" />
    </svg>
  );
}

function IconDownload({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v11" />
      <path d="M8 11l4 4 4-4" />
      <path d="M5 20h14" />
    </svg>
  );
}

function IconLogout({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 4H9a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" />
      <path d="M19 12H9" />
      <path d="M16 15l3-3-3-3" />
    </svg>
  );
}

export default function UserMenu({ currentUser, activePartner, appVersion, appGitSha, appBuildTitle, onNavigate, onSignOut, viewAsUserId, setViewAsUserId, onGoPersonal, viewMode, partners, allPartners, onPartnersChanged, onSwitchPartner }) {
  const [open, setOpen] = useState(false);
  // #2710: Download Scout one click from the menu, labelled with the version this deploy serves.
  const scoutVersion = useScoutVersion();
  const [scoutError, setScoutError] = useState('');
  // #1813 -- real, Stinger direct, own spec gap on #1805: "fold it in as
  // its own section" never said collapsed/accordion, so it shipped as a
  // flat, always-expanded list of all 17 real partners -- pushed Viewing
  // As/Settings/Sign Out off-screen below the fold. Collapsed by default,
  // same real chevron/expand pattern already built tonight for the
  // sidebar (#1797's Dashboard/Hunt/Reports accordions).
  const [personaExpanded, setPersonaExpanded] = useState(false);
  // #1823 -- real, Stinger direct 2026-08-24: real drag-to-reorder +
  // hide/show, right in this dropdown, same visual language already
  // established for DashboardSandbox's widget management (drag handle,
  // hide toggle) rather than a separate settings screen -- proposed
  // this specific plan and got a go-ahead before building. Manage mode
  // renders from allPartners (the full, UNFILTERED list) rather than
  // the already-filtered `partners` prop -- a partner just hidden would
  // otherwise vanish from the very list being used to manage it, with
  // no way back in from here.
  const [personaManageMode, setPersonaManageMode] = useState(false);
  const [personaDragKey, setPersonaDragKey] = useState(null);
  const [personaBusy, setPersonaBusy] = useState(null);
  // #2033 -- real, Stinger direct: "Viewing as" (team-member preview,
  // below) was permanently expanded, the exact "eats real header/
  // dropdown space" problem #1813 already solved for Switch Persona
  // above. Same real collapsed-by-default accordion pattern, same
  // reset-on-close behavior (see the personaExpanded effect below).
  const [viewingAsExpanded, setViewingAsExpanded] = useState(false);
  const managedPartners = [...(allPartners || [])].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  // Real (#1822), Stinger direct, resurfaced tonight -- screenshot of
  // the real live dropdown: "too many in this small area i only want
  // selected partners and requested that was selectable on their
  // partner profile." The flat list was every single active partner
  // (20+) in a small area, exactly the #1813 comment above already
  // flagged as a real blocker before this fix existed.
  //
  // Real fallback: if nobody has been curated yet (no partner has
  // quick_picker=true), fall back to the full list rather than
  // rendering an empty, unusable switcher the moment this ships --
  // the curation only takes effect once Stinger actually checks boxes
  // on partner profiles. Currently active partner always included
  // even if not quick-picked, so you can never lose sight of which
  // partner you're actually on.
  const hasCuratedQuickPicks = partners && partners.some((p) => p.quick_picker === true);
  const quickPersonaList = !partners ? [] : hasCuratedQuickPicks
    ? partners.filter((p) => p.quick_picker === true || p.id === activePartner?.id)
    : partners;

  async function togglePartnerHidden(p) {
    setPersonaBusy(p.id);
    try {
      const { error } = await supabase.from('partners').update({ hide_from_switcher: !p.hide_from_switcher }).eq('id', p.id);
      if (error) throw error;
      onPartnersChanged && await onPartnersChanged();
    } catch (e) {
      console.error('Failed to update partner visibility:', e.message);
    }
    setPersonaBusy(null);
  }

  // Real (#1822) -- same real reasoning as togglePartnerHidden above,
  // separate real column: curates the default Switch Persona short
  // list rather than hiding a partner from the switcher entirely. Both
  // toggles can be right here in Manage mode so curation never
  // requires leaving this dropdown for the full Settings screen.
  async function toggleQuickPicker(p) {
    setPersonaBusy(p.id);
    try {
      const { error } = await supabase.from('partners').update({ quick_picker: !p.quick_picker }).eq('id', p.id);
      if (error) throw error;
      onPartnersChanged && await onPartnersChanged();
    } catch (e) {
      console.error('Failed to update partner quick-pick status:', e.message);
    }
    setPersonaBusy(null);
  }

  async function reorderPartner(dragKey, targetKey) {
    if (dragKey === targetKey) return;
    const ordered = [...managedPartners];
    const fromIdx = ordered.findIndex((p) => p.id === dragKey);
    const toIdx = ordered.findIndex((p) => p.id === targetKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    // Real, matches DataTable's own real reorder pattern -- renumber
    // sequentially rather than trying to swap two values, so the whole
    // list stays a clean, gap-free real order after the move.
    setPersonaBusy('reordering');
    try {
      await Promise.all(ordered.map((p, i) => supabase.from('partners').update({ sort_order: i + 1 }).eq('id', p.id)));
      onPartnersChanged && await onPartnersChanged();
    } catch (e) {
      console.error('Failed to reorder partners:', e.message);
    }
    setPersonaBusy(null);
  }
  // #1813 -- real: collapses again whenever the whole dropdown closes,
  // so every fresh open starts collapsed rather than remembering the
  // last expanded state (which could silently reintroduce the exact
  // same "list eats the dropdown" problem on a later open).
  useEffect(() => {
    if (!open) { setPersonaExpanded(false); setPersonaManageMode(false); setViewingAsExpanded(false); }
  }, [open]);
  // v1.7.41.81 (Ledger #1702) -- real, relocated from SidebarSwitchUser.jsx
  // (bottom-panel pill, removed). Same real plumbing (viewAsUserId /
  // setViewAsUserId from AppContext), same super_admin-only gating, same
  // team_members data -- only the trigger and placement changed, per
  // Stinger's exact ask: "very inconspicuous, very low key," built into
  // the name click rather than a separate styled control.
  const [teamMembers, setTeamMembers] = useState([]);
  const isSuperAdmin = currentUser?.profile?.role === 'super_admin';
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    supabase.from('user_profiles').select('id, full_name, role').order('full_name', { ascending: true })
      .then(({ data }) => { if (!cancelled) setTeamMembers(data || []); });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);
  const wrapRef = useRef(null);

  // Real: close on outside click and on Escape. Both matter for a menu
  // anchored in a header -- without them it stays open over content.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!currentUser?.profile) return null;

  const fullName = currentUser.profile.full_name || '';
  const initials = fullName.split(' ').map((n) => n[0]).join('').slice(0, 2);
  const firstName = fullName.split(' ')[0];

  function go(key) {
    setOpen(false);
    onNavigate && onNavigate(key);
  }

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '9px 14px',
    fontSize: 13,
    fontWeight: 600,
    color: '#e8ecf2',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
  };

  const dividerStyle = { borderBottom: '1px solid rgba(255,255,255,0.08)' };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger -- deliberately identical in appearance to the previous
          static avatar + name, so the header does not visually shift. */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: activePartner?.brand_color || '#0077cc', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700,
        }}>
          {initials}
        </div>
        <span style={{ fontSize: 12, color: 'var(--gov-slate-500)', fontWeight: 500 }}>{firstName}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="#94a3b8"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          minWidth: 240, background: THEME_2026.cardSidebar,
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10,
          boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
          overflow: 'hidden', zIndex: 200,
        }}>
          {/* 1 -- real date header. Wordmark image removed per #1805 --
              was idle visual weight, no functional content. */}
          <div style={{ padding: '11px 14px', ...dividerStyle }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{longDate()}</div>
            <div style={{ fontSize: 11, color: '#8b96a8', marginTop: 1 }}>{fullName}</div>
          </div>

          {/* My Dashboard -- #1805, flatter: star accent kept, heavy
              tinted block background dropped for a plain trailing dot,
              matching the sections below. */}
          <button
            onClick={() => { onGoPersonal && onGoPersonal(); setOpen(false); }}
            style={{ ...itemStyle, ...dividerStyle }}
            onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ width: 18, height: 18, borderRadius: 5, background: 'linear-gradient(135deg, var(--gov-brand-red), #7a1323)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>★</span>
            My Dashboard
            {viewMode === 'personal' && <span style={{ width: 5, height: 5, borderRadius: '50%', background: THEME_2026.brassAccent, flexShrink: 0, marginLeft: 'auto' }} />}
          </button>

          {/* Switch Persona -- #1805 [Goose], real, folded in from
              HeaderPersonaChip (removed as a standalone element in
              App.js). #1813 -- real, collapsed-by-default accordion,
              same chevron/expand pattern already built for the sidebar
              in #1797 -- fixes a real blocker: a flat, always-expanded
              17-partner list was pushing Viewing As/Settings/Sign Out
              off-screen below the fold. */}
          {partners && partners.length > 0 && (
            <div style={dividerStyle}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => setPersonaExpanded((e) => !e)}
                  style={{ ...itemStyle, padding: '8px 14px 8px 14px', flex: 1 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#8b96a8', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
                    Switch Persona
                  </span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#8b96a8" style={{ transform: personaExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>
                {/* #1823 -- real, Stinger direct 2026-08-24: reorder +
                    hide, right in this dropdown, same visual language
                    already established for DashboardSandbox's widget
                    management rather than a separate settings screen. */}
                {personaExpanded && allPartners && (
                  <button
                    onClick={() => setPersonaManageMode((m) => !m)}
                    style={{ fontSize: 10, fontWeight: 700, color: personaManageMode ? THEME_2026.brassAccent : '#8b96a8', background: 'transparent', border: 'none', padding: '8px 14px', cursor: 'pointer', flexShrink: 0 }}
                  >
                    {personaManageMode ? 'Done' : 'Manage'}
                  </button>
                )}
              </div>

              {personaExpanded && !personaManageMode && quickPersonaList.map((p) => {
                const isActive = viewMode !== 'personal' && p.id === activePartner?.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => { onSwitchPartner && onSwitchPartner(p); setOpen(false); }}
                    style={{ ...itemStyle, padding: '6px 14px 6px 14px', fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : '#c3cad6' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: isActive ? THEME_2026.brassAccent : 'transparent',
                      border: isActive ? 'none' : '1px solid #4a5568',
                    }} />
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: `linear-gradient(135deg, ${p.brand_color || '#0077cc'}, ${p.secondary_color || p.brand_color || '#0077cc'})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                      {(p.name || '?')[0]}
                    </span>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </button>
                );
              })}

              {/* #1823 -- real manage mode: renders from allPartners
                  (unfiltered), not `partners` -- a partner just hidden
                  would otherwise vanish from the very list used to
                  manage it, with no way back in from here. Real
                  drag-to-reorder writes sort_order; hide/show writes
                  hide_from_switcher. Neither switches persona while in
                  this mode -- managing and switching don't compete for
                  the same click, same real reasoning as #1812's
                  DataTable grip/sort separation earlier tonight. */}
              {personaExpanded && personaManageMode && managedPartners.map((p) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={() => setPersonaDragKey(p.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); if (personaDragKey) reorderPartner(personaDragKey, p.id); setPersonaDragKey(null); }}
                  style={{ ...itemStyle, padding: '6px 14px 6px 14px', fontSize: 12, opacity: p.hide_from_switcher ? 0.45 : 1 }}
                >
                  <span title="Drag to reorder" style={{ cursor: 'grab', color: '#8b96a8', fontSize: 11, flexShrink: 0 }}>⋮⋮</span>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: `linear-gradient(135deg, ${p.brand_color || '#0077cc'}, ${p.secondary_color || p.brand_color || '#0077cc'})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                    {(p.name || '?')[0]}
                  </span>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#c3cad6', flex: 1 }}>{p.name}</span>
                  <button
                    onClick={() => toggleQuickPicker(p)}
                    disabled={personaBusy === p.id}
                    title={p.quick_picker ? 'In quick list -- click to remove' : 'Click to add to quick list'}
                    style={{ background: 'transparent', border: 'none', cursor: personaBusy === p.id ? 'default' : 'pointer', color: p.quick_picker ? THEME_2026.brassAccent : '#4a5568', fontSize: 13, flexShrink: 0, padding: '2px 4px' }}
                  >
                    {p.quick_picker ? '★' : '☆'}
                  </button>
                  <button
                    onClick={() => togglePartnerHidden(p)}
                    disabled={personaBusy === p.id}
                    title={p.hide_from_switcher ? 'Hidden -- click to show' : 'Click to hide'}
                    style={{ background: 'transparent', border: 'none', cursor: personaBusy === p.id ? 'default' : 'pointer', color: p.hide_from_switcher ? '#8b96a8' : THEME_2026.brassAccent, fontSize: 13, flexShrink: 0, padding: '2px 4px' }}
                  >
                    {p.hide_from_switcher ? '🚫' : '👁'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* v1.7.41.81 (Ledger #1702) -- real, relocated switch-user.
              Deliberately plain rows, "very inconspicuous, very low
              key" was the exact ask. Renders nothing at all for
              non-super_admin, same as the component this replaced --
              genuinely absent, not disabled/greyed.
              #1805 -- restyled to simple dot indicators throughout
              (hollow inactive, filled gold active), matching Switch
              Persona above rather than only marking the active row. */}
          {/* #2033 -- real fix, Stinger direct 2026-09-04: this section
              was permanently expanded -- the exact "eats real dropdown
              space" problem #1813 already fixed for Switch Persona above,
              just never applied here. Same collapsed-by-default accordion
              pattern, and per the ticket's explicit item 6, the active
              member stays visible in the collapsed header row (not just
              "Viewing as" with no indication of who) so collapsing never
              hides who you're currently previewing. */}
          {isSuperAdmin && teamMembers.length > 0 && (() => {
            const realUserId = currentUser?.user?.id;
            const activeId = viewAsUserId || realUserId;
            const activeMember = teamMembers.find((m) => m.id === activeId);
            return (
              <div style={dividerStyle}>
                <button
                  onClick={() => setViewingAsExpanded((e) => !e)}
                  style={{ ...itemStyle, padding: '8px 14px 8px 14px' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#8b96a8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Viewing as
                  </span>
                  {!viewingAsExpanded && activeMember && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#c3cad6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {activeMember.full_name}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#8b96a8" style={{ transform: viewingAsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>
                {viewingAsExpanded && teamMembers.map((m) => {
                  const isActive = m.id === activeId;
                  return (
                    <button
                      key={m.id}
                      onClick={() => { setViewAsUserId && setViewAsUserId(m.id === realUserId ? null : m.id); setOpen(false); }}
                      style={{ ...itemStyle, padding: '6px 14px 6px 24px', fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : '#8b96a8' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                        background: isActive ? THEME_2026.brassAccent : 'transparent',
                        border: isActive ? 'none' : '1px solid #4a5568',
                      }} />
                      {m.full_name}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* Download Scout -- #2710 */}
          <button
            onClick={async () => { setScoutError(''); const r = await downloadScout(scoutVersion); if (!r.ok) setScoutError(r.error); }}
            style={itemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconDownload /> {scoutVersion ? `Download Scout ${scoutVersion}` : 'Download Scout'}
          </button>
          {scoutError && <div role="alert" style={{ padding: '4px 14px 8px', fontSize: 12, color: 'inherit' }}>{scoutError}</div>}

          {/* Settings -- #1805, real inline Tabler-style icon replaces emoji */}
          <button
            onClick={() => go('settings')}
            style={itemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = THEME_2026.hoverState; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconSettings /> Settings
          </button>

          {/* Sign Out -- #1805, real inline Tabler-style icon replaces emoji */}
          <button
            onClick={() => { setOpen(false); onSignOut && onSignOut(); }}
            style={{ ...itemStyle, color: '#f28b82', borderTop: '1px solid rgba(255,255,255,0.08)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(242,139,130,0.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <IconLogout /> Sign Out
          </button>

          {/* Version + health -- #1805, distinct darker strip
              (THEME_2026.primaryBg, darker than the menu's own
              cardSidebar) so it reads as a separate footer zone rather
              than competing with the real menu items above it. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: '8px 14px',
            borderTop: '1px solid rgba(255,255,255,0.08)', background: THEME_2026.primaryBg,
          }}>
            <span
              onClick={() => go('about')}
              data-testid="build-version-menu"
              title={`${appBuildTitle || `Charlie v${appVersion}`} — click for release notes`}
              style={{
                fontSize: 11, fontWeight: 700, color: '#8b96a8',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                letterSpacing: '0.04em', cursor: 'pointer', userSelect: 'none',
              }}
            >
              v{appVersion}
            </span>
            <SystemHealthDot onNavigate={onNavigate} />
          </div>
        </div>
      )}
    </div>
  );
}
